import { useQuery, keepPreviousData } from '@tanstack/react-query';
import type { DataSourceLoadOptions, EnumToken, OrderToken } from '@sleed/bizcommerce-api';
import { ordersApi, skroutzApi } from '~/lib/api/client';
import { applySort, type SortDir } from '~/lib/grid/sort';

export interface SkroutzListParams {
	skip?: number;
	take?: number;
	/** Free-text search (→ searchText: name / email / phone / Skroutz code). */
	search?: string;
	/** Comma-separated id filter (→ searchID, e.g. "100,101,500"). */
	searchID?: string;
	/** Purchase-date sort direction (default desc). */
	sortDir?: SortDir;
}

export interface SkroutzListResult {
	items: OrderToken[];
	totalCount: number;
}

export const skroutzOrderKeys = {
	all: ['skroutz-orders'] as const,
	list: (params: unknown) => [...skroutzOrderKeys.all, 'list', params] as const,
	statuses: () => [...skroutzOrderKeys.all, 'statuses'] as const
};

/**
 * Server-paged Skroutz orders via `ordersApi.getAllSkroutz` (DevExtreme loadOptions envelope, JSON
 * `sort` string per the DataSourceLoadOptionsParser contract). Mirrors the legacy admin's
 * `/orders/getallskroutz` call; the response is `OrderTokenListGrid` (reads `res.data.data` +
 * `res.data.totalCount`). `keepPreviousData` avoids a blank flash on page/search change.
 */
export function useSkroutzOrdersList(params: SkroutzListParams = {}) {
	const { skip = 0, take = 20, search = '', searchID = '', sortDir = 'desc' } = params;
	return useQuery<SkroutzListResult>({
		queryKey: skroutzOrderKeys.list({ skip, take, search, searchID, sortDir }),
		placeholderData: keepPreviousData,
		queryFn: async () => {
			const loadOptions: DataSourceLoadOptions = { skip, take, requireTotalCount: true };
			const extra = loadOptions as Record<string, unknown>;
			if (search.trim()) extra.searchText = search.trim();
			if (searchID.trim()) extra.searchID = searchID.trim();
			applySort(loadOptions, 'pdt', sortDir);
			const res = await ordersApi.getAllSkroutz(loadOptions);
			return { items: res.data.data ?? [], totalCount: res.data.totalCount ?? 0 };
		}
	});
}

/** Order statuses lookup (EnumToken {id, nm}) for resolving the Skroutz status column. */
export function useOrderStatuses() {
	return useQuery<EnumToken[]>({
		queryKey: skroutzOrderKeys.statuses(),
		queryFn: async () => (await ordersApi.getStatuses()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

export type SkroutzExternalLink = 'show_on_skroutz' | 'courier_voucher';

/**
 * Resolve a Skroutz order by its UID and open the requested external URL (Skroutz listing or courier
 * voucher) in a new tab — mirrors the legacy grid's "show on Skroutz" / "print voucher" actions.
 * Returns false when no URL is available for that order.
 */
export async function openSkroutzExternalLink(uid: string, kind: SkroutzExternalLink): Promise<boolean> {
	const order = (await skroutzApi.getOrderByCode(uid)).data;
	const url = order[kind];
	if (!url) return false;
	globalThis.open(url, '_blank', 'noopener,noreferrer');
	return true;
}
